<template>
  <section class="estatus">
    <div class="estatus__header">
      <DsChip label="Tu estatus" variant="orange" />
      <div class="estatus__title">
        <DsTextSplit
            as="h4"
            base="Tu estatus"
            highlight="dentro del sistema"
            baseColor="var(--ds-purple-900)"
            highlightColor="#6D59EA"
          />
      </div>
      <DsText as="body3" class="estatus__intro">
        Cada estatus refleja tu posición actual.
        El que aparece resaltado es donde te encuentras hoy.
      </DsText>
    </div>

    <div class="estatus__timeline">
      <div class="estatus__rail" aria-hidden="true">
        <span
          v-for="step in steps"
          :key="step.number"
          class="estatus__dot"
          :class="{ 'estatus__dot--active': step.active }"
        />
      </div>

      <div class="estatus__cards">
        <article
          v-for="step in steps"
          :key="step.number"
          class="estatus__card"
          :class="{ 'estatus__card--active': step.active }"
        >
          <div class="estatus__card-body">
            <div class="estatus__copy">
              <span v-if="step.active" class="estatus__badge">Estas aquí</span>
              <DsText as="subtitle3" class="section-title_bebas" :color="step.active ? 'rgba(255,255,255,0.98)' : 'var(--ds-secondary-main)'">
                {{ step.number }}
                {{ step.title }}
              </DsText>
              <DsText as="body3" class="estatus__description" :class="{ 'estatus__description--active': step.active }">
                {{ step.description }}
              </DsText>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import DsChip from '../atoms/DsChip.vue'
import DsText from '../atoms/DsText.vue'
import DsTextSplit from '../atoms/DsTextSplit.vue'

const steps = [
  {
    number: '01',
    title: 'Solicitante',
    description: 'Ya realizaste tu contratación',
    active: true,
  },
  {
    number: '02',
    title: 'Integrante',
    description: 'Formas parte activa del grupo',
    active: false,
  },
  {
    number: '03',
    title: 'Adjudicatario',
    description: 'Estás en posibilidad de obtener tu financiamiento',
    active: false,
  },
  {
    number: '04',
    title: 'Adjudicado',
    description: 'Ya obtuviste tu financiamiento',
    active: false,
  },
]
</script>

<style scoped>
.estatus {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.section-title_bebas {
  font-family: var(--ds-font-family-bebas);
}

.estatus__header {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.estatus__title {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
}

.estatus__intro {
  color: var(--ds-text-primary);
  line-height: 1.6;
  max-width: 980px;
}

.estatus__timeline {
  display: grid;
  grid-template-columns: 78px 1fr;
  gap: 28px;
  align-items: stretch;
}

.estatus__rail {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 88px 0 26px;
}

.estatus__rail::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 108px;
  bottom: 46px;
  width: 2px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, rgba(73, 47, 229, 0.45), rgba(73, 47, 229, 0.18));
}

.estatus__dot {
  position: relative;
  z-index: 1;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #d0c8ff;
}

.estatus__dot--active {
  background: #4e34e6;
}

.estatus__cards {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.estatus__card {
  position: relative;
  min-height: 140px;
  border-radius: 28px;
  border: 1px solid #d8ddeb;
  background: #ffffff;
  padding: 28px 32px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding-left: 24px 122px;
}

.estatus__card--active {
  background: #4b31d8;
  border-color: #4b31d8;
  padding-top: 56px;
}

.estatus__badge {
  position: absolute;
  top: 15px;
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ds-secondary-main);
  font-family: var(--ds-font-family-base);
  font-size: var(--ds-subtitle14-size);
  font-weight: var(--ds-subtitle14-weight);
  line-height: 1;
}

.estatus__card-body {
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
}

.estatus__number {
  width: 42px;
  min-width: 42px;
  font-family: var(--ds-font-family-bebas);
  font-size: 32px;
  line-height: 1;
  letter-spacing: 0.02em;
  color: var(--ds-secondary-main);
}

.estatus__card--active .estatus__number {
  color: rgba(255, 255, 255, 0.92);
}

.estatus__copy {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.estatus__description {
  color: var(--ds-text-primary);
  line-height: 1.4;
}

.estatus__description--active {
  color: rgba(255, 255, 255, 0.9);
}

.estatus__card--active .estatus__card-body {
  align-items: flex-start;
}

.estatus__card--active .estatus__copy {
  gap: 14px;
}

@media (max-width: 1024px) {
  .estatus__timeline {
    grid-template-columns: 1fr 5fr;
    gap: 18px;
  }

  .estatus__badge {
    left: 24px;
  }
}

@media (min-width: 1025px) {
  .estatus { gap: 40px; }
  .estatus__header { gap: 16px; }
  .estatus__title { margin-top: 24px; }
  .estatus__intro { line-height: 24px; }
  .estatus__timeline { grid-template-columns: 98px minmax(0, 1fr); gap: 24px; }
  .estatus__cards { gap: 24px; }
  .estatus__card {
    min-height: 136px;
    border-radius: 24px;
    padding: 24px 122px;
  }
  .estatus__card--active { min-height: 192px; padding-top: 80px; }
  .estatus__badge { top: 24px; padding: 8px 13px; line-height: 24px; }
  .estatus__copy { gap: 16px; }
  .estatus__card--active .estatus__copy { gap: 16px; }
  .estatus__description { line-height: 24px; }
  .estatus__rail { padding: 86px 0 84px; }
  .estatus__rail::before { top: 96px; bottom: 94px; }
  .estatus__dot { width: 20px; height: 20px; }
}

@media (max-width: 768px) {
  .estatus { gap: 40px; }
  .estatus__header { gap: 16px; }
  .estatus__title { margin-top: 24px; }
  .estatus__timeline { grid-template-columns: 70px minmax(0, 1fr); gap: 16px; }
  .estatus__card {
    padding: 24px 20px;
    border-radius: 8px;
  }

  .estatus__card--active {
    min-height: 200px;
    padding-top: 76px;
  }

  .estatus__badge {
    top: 20px;
    left: 20px;
    padding: 9px 14px;
  }

  .estatus__card-body {
    align-items: flex-start;
  }

  .estatus__number {
    font-size: 32px;
  }

  .estatus__rail {
    padding: 82px 0 127px;
  }

  .estatus__rail::before {
    top: 92px;
    bottom: 137px;
  }

  .estatus__dot { width: 20px; height: 20px; }
}
</style>
